
import { Link } from "react-router-dom";
import { useTranslatedContent } from "@/hooks/useTranslatedContent";
import { useLanguage } from "@/contexts/LanguageContext";
import { getLocalizedPath } from "@/components/LocalizedRoute";
import { getPhotos, getPhotoUrl } from "@/services/photoService";
import { useEffect, useState, useRef, useCallback } from "react";
import { ChevronDown } from "lucide-react";
import type { Photo } from "@/types/supabase";

interface HeroSectionProps {
  isPageLoaded: boolean;
}

const HeroSection = ({ isPageLoaded }: HeroSectionProps) => {
  const { language } = useLanguage();
  const content = {
    title: "Villa Douce Vague — Location de vacances exclusive au Grau du Roi",
    subtitle: "Villa 4 chambres avec piscine chauffée, à 5 minutes des plages de Méditerranée",
    buttonText: "Réserver maintenant",
    galleryText: "Voir la galerie",
  };

  const { translatedContent, isLoading } = useTranslatedContent(content);
  const [heroPhoto, setHeroPhoto] = useState<Photo | null>(null);

  const bgRef = useRef<HTMLImageElement>(null);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    const load = async () => {
      try {
        const photos = await getPhotos();
        const featured = photos.filter((p) => p.featured);
        if (featured.length > 0) setHeroPhoto(featured[0]);
        else if (photos.length > 0) setHeroPhoto(photos[0]);
      } catch {}
    };
    load();
  }, []);

  // Parallax: background image scrolls at 45% speed + subtle zoom
  const updateParallax = useCallback(() => {
    const img = bgRef.current;
    if (!img) return;
    const scrollY = window.scrollY;
    const viewH = window.innerHeight;
    const progress = scrollY / viewH;
    const translateY = scrollY * 0.45;
    const scale = 1.05 + progress * 0.08;
    img.style.transform = `translateY(${translateY}px) scale(${scale})`;
  }, []);

  useEffect(() => {
    const onScroll = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(updateParallax);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    updateParallax();
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(rafRef.current);
    };
  }, [updateParallax]);

  const stats = [
    { value: "140", unit: "m²" },
    { value: "8", unit: "personnes" },
    { value: "4", unit: "chambres" },
    { value: "5 min", unit: "des plages" },
  ];

  return (
    <section className="relative h-screen min-h-[620px] flex items-end overflow-hidden">
      {/* Background with parallax */}
      <div className="absolute inset-0 z-0">
        {heroPhoto ? (
          <img
            ref={bgRef}
            src={`${getPhotoUrl(heroPhoto.storage_path)}?width=1920&quality=85`}
            alt="Villa Douce Vague"
            className="w-full h-full object-cover"
            style={{ willChange: "transform", transformOrigin: "center top" }}
            fetchPriority="high"
            loading="eager"
          />
        ) : (
          <div className="w-full h-full bg-villa-blue" />
        )}
      </div>

      {/* Cinematic overlay */}
      <div className="absolute inset-0 z-10 hero-gradient" />
      <div
        className="absolute inset-0 z-10 pointer-events-none"
        style={{ background: "radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.35) 100%)" }}
      />

      {/* Content — staggered entrance */}
      <div className="relative z-20 w-full pb-16 md:pb-24 lg:pb-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">

          <p
            className={`eyebrow text-white/55 mb-5 transition-all duration-700`}
            style={{
              opacity: isPageLoaded ? 1 : 0,
              transform: isPageLoaded ? "none" : "translateY(16px)",
              transitionDelay: "100ms",
            }}
          >
            Le Grau du Roi &nbsp;·&nbsp; Méditerranée
          </p>

          <h1
            className="font-playfair text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold text-white leading-[1.04] tracking-tight mb-6 transition-all duration-700"
            style={{
              opacity: isPageLoaded ? 1 : 0,
              transform: isPageLoaded ? "none" : "translateY(24px)",
              transitionDelay: "200ms",
            }}
          >
            {isLoading ? content.title : translatedContent.title}
          </h1>

          <p
            className="text-lg md:text-xl text-white/65 mb-10 max-w-lg transition-all duration-700"
            style={{
              opacity: isPageLoaded ? 1 : 0,
              transform: isPageLoaded ? "none" : "translateY(20px)",
              transitionDelay: "300ms",
            }}
          >
            {isLoading ? content.subtitle : translatedContent.subtitle}
          </p>

          {/* Stats */}
          <div
            className="flex flex-wrap items-center gap-x-6 gap-y-3 mb-10 transition-all duration-700"
            style={{
              opacity: isPageLoaded ? 1 : 0,
              transform: isPageLoaded ? "none" : "translateY(16px)",
              transitionDelay: "400ms",
            }}
          >
            {stats.map((s, i) => (
              <div key={i} className="flex items-baseline gap-1.5">
                {i > 0 && <span className="hidden sm:block w-px h-4 bg-white/20 mr-4" />}
                <span className="text-2xl font-bold text-white font-playfair">{s.value}</span>
                <span className="text-sm text-white/50">{s.unit}</span>
              </div>
            ))}
            <span className="hidden sm:block w-px h-4 bg-white/20" />
            <span className="text-sm text-white/50">Piscine chauffée privée</span>
          </div>

          {/* CTAs */}
          <div
            className="flex flex-col sm:flex-row gap-3 transition-all duration-700"
            style={{
              opacity: isPageLoaded ? 1 : 0,
              transform: isPageLoaded ? "none" : "translateY(16px)",
              transitionDelay: "500ms",
            }}
          >
            <Link
              to={getLocalizedPath("/reserver", language)}
              className="inline-flex items-center justify-center px-8 py-4 bg-villa-turquoise text-white font-medium text-[15px] rounded-full
                         transition-all duration-300 hover:bg-villa-turquoise/90 hover:shadow-2xl hover:shadow-villa-turquoise/30 hover:-translate-y-0.5"
            >
              {isLoading ? "Réserver maintenant" : translatedContent.buttonText}
            </Link>
            <Link
              to={getLocalizedPath("/galerie", language)}
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-medium text-[15px] rounded-full border border-white/20
                         transition-all duration-300 hover:bg-white/18 hover:-translate-y-0.5"
            >
              {isLoading ? "Voir la galerie" : translatedContent.galleryText}
            </Link>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-white/35 transition-all duration-700"
        style={{ opacity: isPageLoaded ? 1 : 0, transitionDelay: "900ms" }}
      >
        <ChevronDown className="h-5 w-5 animate-bounce" strokeWidth={1.5} />
      </div>
    </section>
  );
};

export default HeroSection;
